// booking/components/BookingLayout.jsx

import React from "react";
import { Container, Row, Col, Card, Nav, Button } from "react-bootstrap";
import { FaExclamationTriangle, FaSpinner, FaSyncAlt } from "react-icons/fa";
import { useFormat } from "../../../components/FormatContext";
import { fromCents } from "../utils/money";

const BookingLayout = ({
    tabs,
    activeTab,
    setActiveTab,
    children,
    values,
    recalculate,
    needsRecalc,
    isPricingLoading,
    isHydrating,
    onSubmit,
    isSubmitting,
}) => {
    const { formatCurrency } = useFormat();

    const currentIndex = tabs.findIndex((t) => t.id === activeTab);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <Container fluid className="d-flex flex-column">
            <Row className="justify-content-center">
                <Col sm={12} className="mb-4">
                    <span className="fw-semibold">Motorcycle Information</span>
                </Col>

                {/* 左侧导航 */}
                <Col
                    sm="auto"
                    style={{
                        position: "sticky",
                        top: "10px",
                        height: "fit-content",
                        maxWidth: "280px",
                    }}
                >
                    <Card>
                        <Nav variant="pills" className="flex-column p-3">
                            {tabs.map((tab) => (
                                <Nav.Item key={tab.id}>
                                    <Nav.Link
                                        active={activeTab === tab.id}
                                        onClick={() => handleTabChange(tab.id)}
                                        className="mb-2 rounded"
                                    >
                                        {tab.label}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Card>

                    {/* Summary */}
                    <Card className="mt-2 shadow-sm">
                        <Card.Body className="py-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div>
                                    <div className="fw-semibold">Summary</div>

                                    {needsRecalc && (
                                        <div className="text-warning small d-flex align-items-center gap-1">
                                            <FaExclamationTriangle size={15} />
                                            Changes pending recalculation
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2"
                                    disabled={isPricingLoading || isHydrating}
                                    onClick={recalculate}
                                >
                                    {isPricingLoading ? (
                                        <FaSpinner className="spin" size={15} />
                                    ) : (
                                        <FaSyncAlt size={15} />
                                    )}
                                    <span>
                                        {isPricingLoading ? "Calculating..." : "Recalculate"}
                                    </span>
                                </button>
                            </div>

                            <div className="border-top pt-2">
                                <Row className="g-2">
                                    <Col xs={12} className="d-flex justify-content-between">
                                        <span className="text-muted">Motorcycle</span>
                                        <span className="fw-medium">
                                            {formatCurrency(
                                                fromCents(
                                                    values?.pricingSnapshot?.calculation
                                                        ?.motorcycleTotalWithFeesCents
                                                ),
                                                values?.pricingSnapshot?.calculation
                                                    ?.customerCurrencyCode || "EUR"
                                            )}
                                        </span>
                                    </Col>

                                    <Col xs={12} className="d-flex justify-content-between">
                                        <span className="text-muted">Extras</span>
                                        <span className="fw-medium">
                                            {formatCurrency(
                                                fromCents(
                                                    values?.pricingSnapshot?.calculation
                                                        ?.extrasTotalWithFeesCents
                                                ),
                                                values?.pricingSnapshot?.calculation
                                                    ?.customerCurrencyCode || "EUR"
                                            )}
                                        </span>
                                    </Col>

                                    <Col xs={12} className="d-flex justify-content-between">
                                        <span className="text-muted">Insurance</span>
                                        <span className="fw-medium">
                                            {formatCurrency(
                                                fromCents(
                                                    values?.pricingSnapshot?.calculation
                                                        ?.insurancesTotalCents
                                                ),
                                                values?.pricingSnapshot?.calculation
                                                    ?.customerCurrencyCode || "EUR"
                                            )}
                                        </span>
                                    </Col>

                                    <Col xs={12}>
                                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                            <span className="fw-semibold">Final price</span>
                                            <span className="fw-bold">
                                                {formatCurrency(
                                                    fromCents(
                                                        values?.pricingSnapshot?.calculation
                                                            ?.finalPriceCents
                                                    ),
                                                    values?.pricingSnapshot?.calculation
                                                        ?.customerCurrencyCode || "EUR"
                                                )}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* 右侧内容 */}
                <Col className="flex-grow-1">
                    <Card className="p-4">
                        {children}

                        <div className="d-flex justify-content-end mt-3">
                            {currentIndex < tabs.length - 1 ? (
                                <Button
                                    onClick={() =>
                                        handleTabChange(tabs[currentIndex + 1].id)
                                    }
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    type="submit"
                                    onClick={onSubmit}
                                    disabled={isSubmitting}
                                >
                                    Save
                                </Button>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default React.memo(BookingLayout);